<template>
    <div class="product-select">
        <HeaderMobile>
            <template #left>
                <button
                    type="button"
                    :title="$t('Generic.back')"
                    class="btn btn-link product-select__back-button"
                    @click="onBackButtonTap"
                >
                    <IconSvg filepath="back.svg" />
                </button>
            </template>

            <template #center>
                <p class="product-select__header mb-0">{{ header }}</p>
            </template>
        </HeaderMobile>

        <div class="product-select__body container pt-3 pb-3">
            <!-- Header of product select -->
            <ProductSelectMobileCard
                :product-details="productDetails"
                :class="{ 'mb-3': !isExpired && !isSoldOut }"
                @expired="isExpired = true"
            >
                <template v-if="!noPriceProvided" #right>
                    <ProductSelectHeaderPrice
                        :product-details="productDetails"
                        :is-value-voucher="isValueVoucher"
                        :is-other-product="isOtherProduct"
                        class="product-select__price m-auto"
                    />
                </template>
            </ProductSelectMobileCard>

            <!-- Body of product select -->
            <SoldOutSubscribe v-if="isSoldOut" :product-details="productDetails" />

            <ProductExpiredSubscribe
                v-else-if="isExpired"
                :product-details="productDetails"
                class="product-select__expired"
            />

            <ProductSelectBiddingBody v-else-if="showBiddingForm" :selected-variant="selectedVariant" />

            <template v-else-if="isDirectBookingCalendar">
                <ProductSelectTitle>
                    {{ $t('ProductDetails.productSelect.selectWithArrivalDate') }}
                </ProductSelectTitle>

                <client-only>
                    <Calendar
                        :selected="arrivalDate"
                        :available-dates="productDetails.variantsWithScheduledArrival"
                        @update:selected="dateSelect"
                        gradation-by-price
                    />
                </client-only>
            </template>

            <template v-else>
                <CalendarSelection
                    v-if="isDirectBookingDateShown"
                    :arrival-date="arrivalDate"
                    @back="backToCalendar"
                    class="mt-3 justify-content-center"
                />

                <template v-if="!isOtherProduct">
                    <ProductSelectTitle v-if="isAccessCodeRequired">
                        <template v-if="isDelayPayment"> &nbsp; </template>
                        <template v-else>
                            {{ $t('ProductDetails.productSelect.selectWithCopayment') }}
                        </template>
                    </ProductSelectTitle>

                    <ProductSelectTitle v-else-if="!isUpselling">
                        {{
                            isValueVoucher
                                ? $t('ProductDetails.productSelect.selectVoucher')
                                : $t('ProductDetails.productSelect.selectOffer')
                        }}
                    </ProductSelectTitle>
                </template>

                <UpsellingOptions
                    v-if="isUpselling"
                    :selected-variant="selectedVariant"
                    :variants="variants"
                    @variantSelected="onUpsellVariantSelection"
                />

                <VariantsList
                    v-else
                    :variants="variants"
                    :arrivalDate="arrivalDate"
                    :isAuctionOffer="isAuctionOffer"
                    :isValueVoucher="isValueVoucher"
                    @variantSelected="onFlatVariantSelection"
                />
            </template>
        </div>

        <ProductSelectBottomFooter
            v-if="isUpselling && !isExpired && !isDirectBookingCalendar"
            :selected-variant="selectedVariant"
            :arrivalDate="arrivalDate"
            :checkoutAvailableFrom="productDetails.checkoutAvailableFrom"
            @submit="gtmTrackVariantSelection"
        />
    </div>
</template>

<script>
import HeaderMobile from '@/components/mobile/HeaderMobile.vue';
import ProductSelectBottomFooter from '@/components/product/selection/ProductSelectBottomFooter.vue';
import productSelect from '@/mixins/productSelect';
import ProductSelectMobileCard from '@/components/product/selection/ProductSelectCard.vue';
import ProductSelectHeaderPrice from '@/components/product/selection/ProductSelectHeaderPrice.vue';
import VariantsList from '@/components/product/selection/VariantsList.vue';
import UpsellingOptions from '@/components/product/selection/UpsellingOptions.vue';
import SoldOutSubscribe from '@/components/product/selection/SoldOutSubscribe.vue';
import Calendar from '@/components/product/Calendar.vue';
import Favorite from '@/components/grid/Favorite.vue';
import CalendarSelection from '@/components/product/selection/CalendarSelection.vue';
import ProductSelectTitle from '@/components/product/selection/ProductSelectTitle.vue';
import ProductSelectBiddingBody from '@/components/product/auction/ProductSelectBiddingBody.vue';
import ProductExpiredSubscribe from '@/components/product/countdown/ProductExpiredSubscribe.vue';

export default {
    name: 'ProductSelectMobile',
    mixins: [productSelect],
    components: {
        ProductExpiredSubscribe,
        ProductSelectBiddingBody,
        ProductSelectTitle,
        CalendarSelection,
        Favorite,
        Calendar,
        SoldOutSubscribe,
        UpsellingOptions,
        VariantsList,
        ProductSelectHeaderPrice,
        ProductSelectMobileCard,
        ProductSelectBottomFooter,
        HeaderMobile
    },
    computed: {
        header() {
            if (this.isSoldOut || this.isExpired) {
                return this.$t('ProductDetails.productSelect.waitlist');
            } else if (this.noPriceProvided) {
                return this.$t('ProductDetails.productSelect.selectTrip');
            } else if (this.showBiddingForm) {
                return this.$t('ProductDetails.productSelect.yourBid');
            } else return this.$t('ProductDetails.productSelect.customizeOffer');
        }
    },
    methods: {
        onBackButtonTap() {
            if (this.showBiddingForm) {
                this.selectedVariant = null;
                return;
            }
            this.$emit('close');
        }
    }
};
</script>

<style scoped lang="scss">
.product-select {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    position: relative;
    color: var(--body-color);

    &__header {
        text-transform: uppercase;
    }

    &__body {
        max-width: 586px;
        flex: 1 0 auto;
    }

    &__back-button {
        font-size: 24px;
        color: inherit;
        line-height: 1;
    }
}
</style>
